import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

interface TitleContextType {
  titles: Array<string>;
  push: (title: string) => void;
  pop: () => void;
}

export const TitleContext = createContext<TitleContextType>({
  titles: [],
  push: (title: string) => title,
  pop: () => null,
});

interface Props {
  children: ReactNode;
  baseTitle: string;
}

export function TitleProvider({ children, baseTitle }: Props) {
  const titleStack = useRef<Array<string>>([baseTitle]);

  const setTitle = useCallback((titles: Array<string>) => {
    document.title = titles.join(" | ");
  }, []);

  useEffect(() => {
    setTitle(titleStack.current);
  }, [setTitle]);

  const push = useCallback(
    (title: string) => {
      titleStack.current.push(title);
      setTitle(titleStack.current);
    },
    [setTitle]
  );

  const pop = useCallback(() => {
    titleStack.current.pop();
    setTitle(titleStack.current);
  }, [setTitle]);

  const value = useMemo(
    () => ({
      titles: titleStack.current,
      push,
      pop,
    }),
    [titleStack, push, pop]
  );

  return (
    <TitleContext.Provider value={value}>{children}</TitleContext.Provider>
  );
}
